import type { FC } from 'react';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { getDeviationType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { DeviationTypes } from '@wix/da-shared-react/pkg/types/deviation';
import DeviationPublishedTime from '../DeviationPublishedTime';
import DeviationFlags from '../DeviationFlags';

import s from './DeviationInfo.scss';

interface Props {
  deviation: PapiDeviation;
}

const DeviationInfo: FC<Props> = ({ deviation }) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);

  const deviationType = getDeviationType(deviation);
  const isJournal = deviationType === DeviationTypes.JOURNAL;
  const isStatusUpdate = deviationType === DeviationTypes.STATUS;
  const showPostType = isMobile && (isJournal || isStatusUpdate);

  const showFlags = useMemo(
    () =>
      !isMobile && (deviation.isMature || deviation.isAiUseDisallowed || deviation.isAiGenerated),
    [deviation, isMobile]
  );

  const showSeparator = showFlags || showPostType;

  return (
    <div className={s['root']}>
      <DeviationPublishedTime className={s['published-time']} deviation={deviation} />
      {showSeparator && <div className={s['separator']} />}
      {showFlags && <DeviationFlags className={s['flags']} deviation={deviation} />}
      {showPostType && (
        <div className={s['post-type']}>
          {isJournal && t('studio.table.postType.journal')}
          {isStatusUpdate && t('studio.table.postType.status')}
        </div>
      )}
    </div>
  );
};
DeviationInfo.displayName = 'DeviationInfo';

export default DeviationInfo;
